import { Fragment, useEffect, useState, useMemo, useRef } from "react";
import {
  Dialog,
  Disclosure,
  Menu,
  Transition,
} from "@headlessui/react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  ChevronDownIcon,
  FunnelIcon,
  MinusIcon,
  PlusIcon,
} from "@heroicons/react/20/solid";
import baseURL from "../../../utils/baseURL";
import { fetchProductsAction } from "../../../redux/slices/products/productSlices";
import { fetchBrandsAction } from "../../../redux/slices/categories/brandsSlice";
import { fetchStylesAction } from "../../../redux/slices/categories/stylesSlice";
import Skeleton from "../../Skeleton/Skeleton";
import ErrorMsg from "../../ErrorMessage/ErrorMsg";
import NoDataFound from "../../NoDataFound/NoDataFound";
import { getOptimizedImage } from '../../../utils/cloudinary';

const sortOptions = [
  { name: "Most Popular", value: "popular" },
  { name: "Best Rating", value: "rating" },
  { name: "Newest", value: "newest" },
  { name: "Price: Low to High", value: "price-asc" },
  { name: "Price: High to Low", value: "price-desc" },
];

const allPrice = [
  { amount: "0 - 50" },
  { amount: "50 - 100" },
  { amount: "100 - 150" },
  { amount: "150 - 200" },
  { amount: "200 - 250" },
  { amount: "250 - 300" },
  { amount: "300 - 350" },
  { amount: "350 - 400" },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function ProductsFilters() {
  const dispatch = useDispatch();
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [params, setParams] = useSearchParams();
  const category = params.get("category");

  // Filter states
  const [style, setStyle] = useState("");
  const [price, setPrice] = useState("");
  const [brand, setBrand] = useState("");
  const [size, setSize] = useState("");
  const [sortBy, setSortBy] = useState("popular");
  const [noResults, setNoResults] = useState(false);
  const [ready, setReady] = useState(false);

  // 1. Initialize filters from URL once
  useEffect(() => {
    const brandParam = params.get("brand");
    const sizeParam = params.get("size");
    const priceParam = params.get("price");
    const styleParam = params.get("style");
    const sortParam = params.get("sort");

    if (brandParam) setBrand(brandParam);
    if (sizeParam) setSize(sizeParam);
    if (priceParam) setPrice(priceParam);
    if (styleParam) setStyle(styleParam);
    if (sortParam) setSortBy(sortParam);

    setReady(true);
  }, []); // eslint-disable-line

  // 2. Sync filters back to URL (skip first render)
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const newParams = new URLSearchParams();
    if (category) newParams.set("category", category);
    if (brand) newParams.set("brand", brand);
    if (size) newParams.set("size", size);
    if (price) newParams.set("price", price);
    if (style) newParams.set("style", style);
    if (sortBy) newParams.set("sort", sortBy);
    setParams(newParams, { replace: true });
  }, [category, brand, size, price, style, sortBy, setParams]);

  // 3. Fetch brands and styles once
  useEffect(() => {
    dispatch(fetchBrandsAction());
    dispatch(fetchStylesAction());
  }, [dispatch]);

  // 4. Build URL for product fetch (backend only handles filters, sorting we do client‑side)
  let productUrl = `${baseURL}/products`;
  const queryParams = new URLSearchParams();
  if (category) queryParams.append("category", category);
  if (brand) queryParams.append("brand", brand);
  if (size) queryParams.append("size", size);
  if (price) queryParams.append("price", price);
  if (style) queryParams.append("style", style);
  const queryString = queryParams.toString();
  if (queryString) productUrl = `${productUrl}?${queryString}`;

  // 5. Fetch products only when ready
  useEffect(() => {
    if (!ready) return;
    dispatch(fetchProductsAction({ url: productUrl }));
  }, [dispatch, productUrl, ready]);

  // Get store data
  const { products: { products }, loading, error } = useSelector((state) => state?.products) || {};
  const { brands: { brands } } = useSelector((state) => state?.brands);
  const { styles: { styles } } = useSelector((state) => state?.styles);

  // Client‑side sorting
  const sortedProducts = useMemo(() => {
    if (!products) return [];
    const arr = [...products];
    switch (sortBy) {
      case "price-asc":
        return arr.sort((a, b) => a.price - b.price);
      case "price-desc":
        return arr.sort((a, b) => b.price - a.price);
      case "rating":
        return arr.sort((a, b) => (b.averageRating || 0) - (a.averageRating || 0));
      case "newest":
        return arr.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      default: // popular (by totalSold)
        return arr.sort((a, b) => (b.totalSold || 0) - (a.totalSold || 0));
    }
  }, [products, sortBy]);

  // Available filter options based on current products
  const availableBrands = useMemo(() => {
    if (!products || products.length === 0) return [];
    const brandNames = new Set(products.map(p => p.brand).filter(Boolean));
    return brands?.filter(b => brandNames.has(b.name)) || [];
  }, [products, brands]);

  const availableStyles = useMemo(() => {
    if (!products || products.length === 0) return [];
    const styleNames = new Set(products.map(p => p.style).filter(Boolean));
    return styles?.filter(s => styleNames.has(s.name)) || [];
  }, [products, styles]);

  const availableSizes = useMemo(() => {
    if (!products || products.length === 0) return [];
    const sizeNames = new Set(products.map(p => p.size).filter(Boolean));
    return Array.from(sizeNames).sort();
  }, [products]);

  const availablePriceRanges = useMemo(() => {
    if (!products || products.length === 0) return allPrice;
    const prices = products.map(p => p.price).filter(p => p);
    if (prices.length === 0) return allPrice;
    return allPrice.filter(range => {
      const [rangeMin, rangeMax] = range.amount.split(' - ').map(Number);
      return products.some(product => {
        const productPrice = product.price;
        if (rangeMax) {
          return productPrice >= rangeMin && productPrice <= rangeMax;
        } else {
          return productPrice >= rangeMin;
        }
      });
    });
  }, [products]);

  useEffect(() => {
    if (!loading && products && products.length === 0) {
      setNoResults(true);
    } else {
      setNoResults(false);
    }
  }, [products, loading]);

  const resetAllFilters = () => {
    setStyle("");
    setPrice("");
    setBrand("");
    setSize("");
    setSortBy("popular");
    setNoResults(false);
    const newParams = new URLSearchParams();
    if (category) newParams.set("category", category);
    setParams(newParams);
    setMobileFiltersOpen(false);
  };

  const activeFiltersCount = [style, price, brand, size].filter(Boolean).length;

  // Helper to render filter sections (reused for mobile & desktop)
  const renderFilterSections = (isMobile = false) => (
    <>
      {/* Style filter */}
      {availableStyles.length > 0 && (
        <Disclosure as="div" className="border-t border-gray-200 px-5 py-6">
          {({ open }) => (
            <>
              <h3 className="-mx-2 -my-3 flow-root">
                <Disclosure.Button className="flex w-full items-center justify-between bg-white px-2 py-4 text-gray-400 hover:text-gray-500">
                  <span className="text-base font-medium text-gray-900">Style</span>
                  <span className="ml-6 flex items-center">
                    {open ? <MinusIcon className="h-5 w-5" /> : <PlusIcon className="h-5 w-5" />}
                  </span>
                </Disclosure.Button>
              </h3>
              <Disclosure.Panel className="pt-4 pb-2">
                <div className="space-y-3">
                  {availableStyles.map((styleItem) => (
                    <div key={styleItem._id} className="flex items-center">
                      <input
                        onChange={() => {
                          const newStyle = style === styleItem.name ? "" : styleItem.name;
                          setStyle(newStyle);
                          if (isMobile) setMobileFiltersOpen(false);
                        }}
                        type="radio"
                        checked={style === styleItem.name}
                        className="h-5 w-5 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <label className="ml-3 text-base text-gray-700">{styleItem.name}</label>
                    </div>
                  ))}
                </div>
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      )}

      {/* Price filter */}
      {availablePriceRanges.length > 0 && (
        <Disclosure as="div" className="border-t border-gray-200 px-5 py-6">
          {({ open }) => (
            <>
              <h3 className="-mx-2 -my-3 flow-root">
                <Disclosure.Button className="flex w-full items-center justify-between bg-white px-2 py-4 text-gray-400 hover:text-gray-500">
                  <span className="text-base font-medium text-gray-900">Price</span>
                  <span className="ml-6 flex items-center">
                    {open ? <MinusIcon className="h-5 w-5" /> : <PlusIcon className="h-5 w-5" />}
                  </span>
                </Disclosure.Button>
              </h3>
              <Disclosure.Panel className="pt-4 pb-2">
                <div className="space-y-3">
                  {availablePriceRanges.map((priceOption) => (
                    <div key={priceOption.amount} className="flex items-center">
                      <input
                        onChange={() => {
                          const newPrice = price === priceOption.amount ? "" : priceOption.amount;
                          setPrice(newPrice);
                          if (isMobile) setMobileFiltersOpen(false);
                        }}
                        type="radio"
                        checked={price === priceOption.amount}
                        className="h-5 w-5 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <label className="ml-3 text-base text-gray-700">${priceOption.amount}</label>
                    </div>
                  ))}
                </div>
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      )}

      {/* Brand filter */}
      {availableBrands.length > 0 && (
        <Disclosure as="div" className="border-t border-gray-200 px-5 py-6">
          {({ open }) => (
            <>
              <h3 className="-mx-2 -my-3 flow-root">
                <Disclosure.Button className="flex w-full items-center justify-between bg-white px-2 py-4 text-gray-400 hover:text-gray-500">
                  <span className="text-base font-medium text-gray-900">Brand</span>
                  <span className="ml-6 flex items-center">
                    {open ? <MinusIcon className="h-5 w-5" /> : <PlusIcon className="h-5 w-5" />}
                  </span>
                </Disclosure.Button>
              </h3>
              <Disclosure.Panel className="pt-4 pb-2">
                <div className="space-y-3">
                  {availableBrands.map((brandItem) => (
                    <div key={brandItem._id} className="flex items-center">
                      <input
                        onChange={() => {
                          const newBrand = brand === brandItem.name ? "" : brandItem.name;
                          setBrand(newBrand);
                          if (isMobile) setMobileFiltersOpen(false);
                        }}
                        type="radio"
                        checked={brand === brandItem.name}
                        className="h-5 w-5 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <label className="ml-3 text-base text-gray-700">{brandItem.name}</label>
                    </div>
                  ))}
                </div>
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      )}

      {/* Size filter */}
      {availableSizes.length > 0 && (
        <Disclosure as="div" className="border-t border-gray-200 px-5 py-6">
          {({ open }) => (
            <>
              <h3 className="-mx-2 -my-3 flow-root">
                <Disclosure.Button className="flex w-full items-center justify-between bg-white px-2 py-4 text-gray-400 hover:text-gray-500">
                  <span className="text-base font-medium text-gray-900">Size</span>
                  <span className="ml-6 flex items-center">
                    {open ? <MinusIcon className="h-5 w-5" /> : <PlusIcon className="h-5 w-5" />}
                  </span>
                </Disclosure.Button>
              </h3>
              <Disclosure.Panel className="pt-4 pb-2">
                <div className="space-y-3">
                  {availableSizes.map((sizeOption) => (
                    <div key={sizeOption} className="flex items-center">
                      <input
                        onChange={() => {
                          const newSize = size === sizeOption ? "" : sizeOption;
                          setSize(newSize);
                          if (isMobile) setMobileFiltersOpen(false);
                        }}
                        type="radio"
                        checked={size === sizeOption}
                        className="h-5 w-5 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <label className="ml-3 text-base text-gray-700">{sizeOption}</label>
                    </div>
                  ))}
                </div>
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      )}
    </>
  );

  return (
    <div className="bg-white">
      {/* Mobile filter dialog */}
      <Transition.Root show={mobileFiltersOpen} as={Fragment}>
        <Dialog as="div" className="relative z-40 lg:hidden" onClose={setMobileFiltersOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="relative ml-auto flex h-full w-full max-w-xs flex-col overflow-y-auto bg-white py-6 shadow-xl">
                <div className="flex items-center justify-between px-5">
                  <h2 className="text-xl font-semibold text-gray-900">Filters</h2>
                  <button
                    type="button"
                    className="-mr-2 flex h-12 w-12 items-center justify-center rounded-full bg-white text-gray-500 hover:bg-gray-100"
                    onClick={() => setMobileFiltersOpen(false)}
                  >
                    <span className="sr-only">Close menu</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <form className="mt-4 border-t border-gray-200">
                  {renderFilterSections(true)}
                  {activeFiltersCount > 0 && (
                    <div className="border-t border-gray-200 px-5 py-6">
                      <button
                        onClick={resetAllFilters}
                        className="w-full rounded-lg bg-red-600 py-4 text-base font-semibold text-white shadow-sm hover:bg-red-700"
                      >
                        Clear all filters ({activeFiltersCount})
                      </button>
                    </div>
                  )}
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between border-b border-gray-200 pb-4 pt-6 sm:pt-8">
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
            {category
              ? category.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')
              : "All Products"}
          </h1>
          <div className="flex items-center">
            {activeFiltersCount > 0 && (
              <div className="mr-2 sm:mr-4">
                <button
                  onClick={resetAllFilters}
                  className="text-sm font-medium text-red-600 hover:text-red-800"
                >
                  Clear
                </button>
              </div>
            )}
            <Menu as="div" className="relative inline-block text-left">
              <div>
                <Menu.Button className="group inline-flex items-center justify-center rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900">
                  Sort
                  <ChevronDownIcon className="-mr-1 ml-1 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500" />
                </Menu.Button>
              </div>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 z-10 mt-2 w-40 origin-top-right rounded-md bg-white shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <div className="py-1">
                    {sortOptions.map((option) => (
                      <Menu.Item key={option.value}>
                        {({ active }) => (
                          <button
                            onClick={() => setSortBy(option.value)}
                            className={classNames(
                              sortBy === option.value ? "font-medium text-gray-900" : "text-gray-500",
                              active ? "bg-gray-100" : "",
                              "block w-full text-left px-4 py-2 text-sm"
                            )}
                          >
                            {option.name}
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </div>

        <section aria-labelledby="products-heading" className="pb-16 pt-4">
          <div className="grid grid-cols-1 gap-x-6 gap-y-8 lg:grid-cols-4">
            {/* Desktop filters */}
            <form className="hidden lg:block">
              {renderFilterSections(false)}
            </form>

            {/* Product grid */}
            <div className="lg:col-span-3">
              {loading ? (
               <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-3">
  {Array.from({ length: 4 }).map((_, i) => (
    <div key={i} className="space-y-4">
      {/* Bigger image placeholder */}
      <Skeleton className="aspect-[3/4] w-full rounded-lg" />
      <div className="space-y-2">
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-6 w-1/3" />
      </div>
    </div>
  ))}
</div>
              ) : error ? (
                <ErrorMsg message={error?.message} />
              ) : noResults ? (
                <div className="py-12 text-center">
                  <NoDataFound message="No products found with the selected filters." />
                  <button
                    onClick={resetAllFilters}
                    className="mt-6 inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-5 py-3 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
                  >
                    Clear all filters
                  </button>
                </div>
              ) : sortedProducts.length === 0 ? (
                <NoDataFound message="No products available at the moment." />
              ) : (
                <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-3">
                  {sortedProducts.map((product) => (
                    <div key={product._id} className="group relative">
                      <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden rounded-lg bg-gray-100">
                          <img
                            src={getOptimizedImage(product?.images[0], { width: 400, height: 400, quality: 'auto', format: 'auto' })}
                            alt={product?.name}
                            className="h-full w-full object-cover object-center"
                          />
                      </div>
                      <div className="mt-3">
                        <h3 className="text-sm font-medium text-gray-900 line-clamp-1">
                          <Link to={`/products/${product._id}`}>
                            <span aria-hidden="true" className="absolute inset-0" />
                            {product.name}
                          </Link>
                        </h3>
                        <p className="mt-1 text-xs text-gray-500 line-clamp-1">
                          {product?.brand?.name || product?.brand}
                        </p>
                        <p className="mt-1 text-sm font-semibold text-gray-900">
                          ${product.price}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      {/* Sticky filter button (mobile only) */}
      <div className="fixed bottom-4 left-4 right-4 z-30 lg:hidden">
        <button
          onClick={() => setMobileFiltersOpen(true)}
          className="flex w-full items-center justify-center rounded-full bg-indigo-600 px-6 py-4 text-base font-semibold text-white shadow-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        >
          <FunnelIcon className="mr-2 h-5 w-5" />
          Filter & Sort
          {activeFiltersCount > 0 && (
            <span className="ml-2 rounded-full bg-white px-2 py-0.5 text-xs font-medium text-indigo-600">
              {activeFiltersCount}
            </span>
          )}
        </button>
      </div>
    </div>
  );
}